import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

const FloatingBubbles = () => {
  const mountRef = useRef(null);

  useEffect(() => {
    const currentMount = mountRef.current;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    currentMount.appendChild(renderer.domElement);

    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, 'rgba(217, 234, 253, 0.1)');
    gradient.addColorStop(0.3, 'rgba(89, 146, 210, 0.2)');
    gradient.addColorStop(0.7, 'rgba(45, 64, 89, 0.15)');
    gradient.addColorStop(1, 'rgba(45, 64, 89, 0)');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 64, 64);

    const bubbleTexture = new THREE.Texture(canvas);
    bubbleTexture.needsUpdate = true;

    const bubbles = [];
    const bubbleCount = 40;

    for (let i = 0; i < bubbleCount; i++) {
      const geometry = new THREE.SphereGeometry(Math.random() * 0.8 + 0.4, 32, 32);
      const material = new THREE.MeshPhongMaterial({
        map: bubbleTexture,
        transparent: true,
        opacity: Math.random() * 0.25 + 0.15,
        shininess: 90,
        specular: new THREE.Color(0x5592d2),
        blending: THREE.AdditiveBlending,
        depthWrite: false
      });

      const bubble = new THREE.Mesh(geometry, material);

      bubble.position.x = Math.random() * 50 - 25;
      bubble.position.y = Math.random() * 50 - 25;
      bubble.position.z = Math.random() * 20 - 10;

      bubble.userData = {
        speedY: Math.random() * 0.015 + 0.008,
        amplitude: Math.random() * 0.4 + 0.3,
        phase: Math.random() * Math.PI * 2,
        rotationSpeed: Math.random() * 0.01 - 0.005
      };

      bubbles.push(bubble);
      scene.add(bubble);
    }

    const ambientLight = new THREE.AmbientLight(0xd9eafd, 0.7);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0x5592d2, 0.8);
    pointLight.position.set(10, 10, 10);
    scene.add(pointLight);

    camera.position.z = 30;

    let mouseX = 0;
    let mouseY = 0;
    const windowHalfX = window.innerWidth / 2;
    const windowHalfY = window.innerHeight / 2;

    const onDocumentMouseMove = (event) => {
      mouseX = (event.clientX - windowHalfX) / 150;
      mouseY = (event.clientY - windowHalfY) / 150;
    };

    document.addEventListener('mousemove', onDocumentMouseMove);

    const animate = () => {
      requestAnimationFrame(animate);

      bubbles.forEach((bubble) => {
        bubble.position.y += bubble.userData.speedY;
        bubble.position.x += Math.sin(Date.now() * 0.001 + bubble.userData.phase) * bubble.userData.amplitude * 0.015;

        if (bubble.position.y > 25) {
          bubble.position.y = -25;
          bubble.position.x = Math.random() * 50 - 25;
        }

        bubble.rotation.x += bubble.userData.rotationSpeed;
        bubble.rotation.y += bubble.userData.rotationSpeed;
      });

      camera.position.x += (mouseX - camera.position.x) * 0.03;
      camera.position.y += (-mouseY - camera.position.y) * 0.03;
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      currentMount.removeChild(renderer.domElement);
      document.removeEventListener('mousemove', onDocumentMouseMove);
      window.removeEventListener('resize', handleResize);

      bubbles.forEach(bubble => {
        bubble.geometry.dispose();
        bubble.material.dispose();
      });
      renderer.dispose();
    };
  }, []);

  return (
    <div
      ref={mountRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        pointerEvents: 'none',
        opacity: 0.9
      }}
    />
  );
};

export default FloatingBubbles;